import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isNiche, type Niche } from '../lib/niche';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Código do catálogo de permissões (ex.: 'users.manage'). A rota exige
   * exatamente essa permissão — quem decide se o usuário a tem é o banco
   * (role_permissions / company_role_permissions), não este componente. */
  requiredPermission?: string;
  /** Telas de um segmento só (ex.: Gerador ACM, Turmas). Fora dele a rota
   * não abre, igual ao menu, que já esconde o item. */
  niche?: Niche;
  /** Módulo em companies.modules_enabled que libera a tela fora do segmento. */
  module?: string;
  /** Telas liberadas só para algumas empresas (ex.: Banco de Talentos). */
  companyGate?: (company: ReturnType<typeof useAuth>['company']) => boolean;
}

const Blocked: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div className="flex items-center justify-center h-full min-h-[50vh]">
    <div className="text-center bg-white/70 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 dark:border-gray-700 max-w-md mx-4" role="alert">
      <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <span className="text-red-700 text-2xl" aria-hidden="true">🔒</span>
      </div>
      <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{title}</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-4">{message}</p>
      <button
        onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = '/'))}
        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Voltar
      </button>
    </div>
  </div>
);

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermission, niche, module, companyGate }) => {
  const { user, company, isAuthenticated, loading, isSuperAdmin, permissions } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  if (user.is_active === false) {
    return <Navigate to="/" replace />;
  }

  // Se for superadmin (global ou da matriz), tem acesso a tudo
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  if (requiredPermission && !permissions.includes(requiredPermission)) {
    return <Blocked title="Acesso Negado" message="Você não tem permissão para acessar esta página. Se precisar dela, peça ao gestor da sua empresa." />;
  }

  if (companyGate && !companyGate(company)) {
    return <Blocked title="Tela indisponível" message="Esta página não está liberada para a sua empresa." />;
  }

  if (niche && !isNiche(company, niche) && !(module && Array.isArray(company?.modules_enabled) && company.modules_enabled.includes(module))) {
    return <Blocked title="Tela de outro segmento" message="Esta página não faz parte do segmento da sua empresa." />;
  }

  return <>{children}</>;
};
